import type { EnrichmentRunSelection, IngestMode } from '~/components/common/enrichment-run-panel';
import type { EnrichmentPostStepFlags } from '~/types/enrichment-flags';
import { compactEnrichmentFlags } from '~/types/enrichment-flags';

import { GMC_ADS_FIELDS, type GmcAdsField } from '../types/content-scoring.types';
import type { EnrichmentRunConfig } from '../types/enrichment-run-config';
import { compactEnrichmentRunConfig } from '../types/enrichment-run-config';

/**
 * Config-service attribute keys that name a POST-phase enrichment **step** rather
 * than a content field. They are not members of `GmcAdsField` and never appear in
 * `contentAttributes`; they drive the `searchDescriptors` object instead, which is
 * why the panel can offer one control per outcome rather than a pill and a toggle
 * that disagree.
 */
const STEP_KEYS = {
  searchDescriptors: 'search_descriptors',
  phraseDescriptions: 'phrase_descriptions',
} as const;

/** Runtime membership check for {@link toGmcAdsField} — see that function. */
const GMC_ADS_FIELD_SET = new Set<string>(GMC_ADS_FIELDS);

/**
 * The request slices derived from one panel selection. Kept as slices rather than
 * one flat body because the two trigger routes disagree on both shape and
 * placement:
 *
 * - `enrichments/scheduled/jobs` (+ its three triggers) take the **nested**
 *   `runConfig` at the top level.
 * - `catalog-operations/trigger` takes `fields` and the **flat** post-step flags,
 *   both nested on its `useCase` object, per the backend UI contract.
 */
export interface EnrichmentRunPayload {
  /** Omitted entirely when the operator left the name blank — the name is optional. */
  name?: string;
  /** For the batch create only: the server returns the batch it already made for this key. */
  idempotencyKey?: string;
  /** Nested run configuration for the scheduled-enrichment routes. */
  runConfig: EnrichmentRunConfig;
  /**
   * google-agent retailer prompt slug for `catalog-operations/trigger`'s `useCase`. The scheduled
   * routes carry the same value inside `runConfig`. Omitted when no retailer was chosen.
   */
  retailer?: string;
  /** Locales for `catalog-operations/trigger`'s `useCase`; the scheduled routes carry them inside
   *  `runConfig`. Omitted when English only. */
  targetLocales?: string[];
  /** `useCase.fields` for `catalog-operations/trigger` — the selected content
   *  fields in enum form. Omitted when nothing is selected. */
  fields?: GmcAdsField[];
  /** Flat post-step flags, still the shape `catalog-operations/trigger` specifies. */
  flags: EnrichmentPostStepFlags;
  /** Bypass the enrichment cache and regenerate fresh. Sent top-level on the
   *  scheduled routes and on `catalog-operations/trigger`'s `useCase`; an explicit
   *  operator choice, so always present. */
  overrideCache: boolean;
  /**
   * Curation's ingest scope for the run. Sent top-level, beside `overrideCache`,
   * rather than inside `runConfig` — `runConfig` describes which FIELDS get
   * enriched, while this decides which PRODUCTS enter the run at all.
   *
   * Absent when the surface did not offer the control, which leaves the server's
   * per-flow default in charge.
   */
  ingestMode?: IngestMode;
}

/**
 * Converts one panel selection into every request slice the enrichment-run
 * triggers need. The single seam between the picker and the wire — every surface
 * calls this, so a contract change lands in one place.
 *
 * Selected keys split two ways: the two step-shaped keys become the
 * `searchDescriptors` object (and its flat-flag equivalent), and everything else
 * becomes `contentAttributes` plus its `fields` enum form. Scoring is always an
 * explicit pair, because the operator's two switches are always a decision.
 */
export function buildEnrichmentRunPayload(selection: EnrichmentRunSelection): EnrichmentRunPayload {
  const selected = new Set(selection.attributeKeys);
  const available = new Set(selection.availableKeys);
  const fieldKeys = selection.attributeKeys.filter(
    (key) => key !== STEP_KEYS.searchDescriptors && key !== STEP_KEYS.phraseDescriptions,
  );

  // Only assert the descriptor steps when the taxonomy actually offered them. A
  // tenant whose config service predates these attributes sends no
  // `searchDescriptors` object at all, keeping the server default rather than an
  // explicit `false` that would override it.
  const offersDescriptors =
    available.has(STEP_KEYS.searchDescriptors) || available.has(STEP_KEYS.phraseDescriptions);
  const searchDescriptors = offersDescriptors
    ? {
        enabled: selected.has(STEP_KEYS.searchDescriptors),
        phraseDescriptions: selected.has(STEP_KEYS.phraseDescriptions),
      }
    : undefined;

  const scoring = { preScoring: selection.scoring.before, postScoring: selection.scoring.after };

  // Keys the taxonomy offers that aren't in the backend's GMC enum (e.g. a
  // Meta-only key, or a newer attribute this FE union hasn't caught up to yet)
  // are dropped rather than shipped uppercased — an unrecognised enum name is
  // an HTTP 400, not a silent drop, on `catalog-operations/trigger`.
  const gmcAdsFields = fieldKeys
    .map(toGmcAdsField)
    .filter((f): f is GmcAdsField => f !== undefined);

  // The same four decisions in the flat form the contract route still specifies.
  const flags: EnrichmentPostStepFlags = {
    scoringPreEnabled: scoring.preScoring,
    scoringPostEnabled: scoring.postScoring,
    ...(available.has(STEP_KEYS.searchDescriptors)
      ? { searchDescriptorsEnabled: selected.has(STEP_KEYS.searchDescriptors) }
      : {}),
    ...(available.has(STEP_KEYS.phraseDescriptions)
      ? { phraseDescriptionsEnabled: selected.has(STEP_KEYS.phraseDescriptions) }
      : {}),
  };

  return {
    ...(selection.name ? { name: selection.name } : {}),
    runConfig: compactEnrichmentRunConfig({
      contentAttributes: fieldKeys,
      ...(searchDescriptors ? { searchDescriptors } : {}),
      scoring,
      ...(selection.retailer ? { retailer: selection.retailer } : {}),
      ...(selection.targetLocales?.length ? { targetLocales: selection.targetLocales } : {}),
    }),
    ...(selection.retailer ? { retailer: selection.retailer } : {}),
    ...(selection.targetLocales?.length ? { targetLocales: selection.targetLocales } : {}),
    ...(gmcAdsFields.length > 0 ? { fields: gmcAdsFields } : {}),
    flags: compactEnrichmentFlags(flags),
    overrideCache: selection.overrideCache,
    // Passed through untouched: an absent key on the selection stays absent here,
    // so the server's per-flow default survives the round trip.
    ...(selection.ingestMode !== undefined ? { ingestMode: selection.ingestMode } : {}),
    ...(selection.idempotencyKey ? { idempotencyKey: selection.idempotencyKey } : {}),
  };
}

/**
 * Config key → `GmcAdsField`, or `undefined` when the uppercased key isn't a
 * member of the backend's GMC enum. Most keys are the SCREAMING_SNAKE form of
 * the taxonomy key mechanically, but the enum has no `@JsonCreator` and no
 * leniency, so a key the enum doesn't recognise must be filtered out here
 * rather than shipped and 400 the whole request.
 */
function toGmcAdsField(key: string): GmcAdsField | undefined {
  const upper = key.toUpperCase();
  return GMC_ADS_FIELD_SET.has(upper) ? (upper as GmcAdsField) : undefined;
}
